import { supportSessionApi } from '../api/im/supportSessionApi';
import { IM_API } from '../constants/endpoints';
import { MEMBER_STORAGE_KEYS } from '../constants/storageKeys';

/**
 * Resolve chat + support entry switches（§51）。
 * On failure prefer OFF（勿默認打開第三方，避免「全關意圖卻露出 CMS」）。
 */
export async function fetchCustomerServiceChannelMode() {
  try {
    const data = await supportSessionApi.getChannelMode();
    const supportMode = normalizeSupportMode(data?.supportMode || data?.channelMode);
    const chatEnabled = !!data?.chatEnabled;
    return {
      chatEnabled,
      supportMode,
      channelMode: supportMode,
      formosaImEnabled: data?.formosaImEnabled ?? supportMode === 'FORMOSA_IM',
      thirdPartyCmsEnabled: data?.thirdPartyCmsEnabled ?? supportMode === 'THIRD_PARTY',
    };
  } catch {
    return {
      chatEnabled: false,
      supportMode: 'OFF',
      channelMode: 'OFF',
      formosaImEnabled: false,
      thirdPartyCmsEnabled: false,
    };
  }
}

function normalizeSupportMode(raw) {
  const mode = String(raw || '')
    .trim()
    .toUpperCase();
  if (mode === 'HYBRID') return 'FORMOSA_IM';
  if (mode === 'OFF' || mode === 'FORMOSA_IM' || mode === 'THIRD_PARTY') return mode;
  return 'OFF';
}

/**
 * Start or resume IM Support Session and return session payload.
 */
export async function startOrResumeSupportSession(options = {}) {
  const {
    category,
    language,
    businessSystem,
    businessType,
    businessId,
    priority,
  } = options;
  return supportSessionApi.start({
    category,
    language,
    businessSystem,
    businessType,
    businessId,
    priority,
  });
}

export function isTerminalSupportStatus(status) {
  return status === 'ENDED' || status === 'ABANDONED';
}

export function canMemberSendInSupport(status) {
  if (!status) return false;
  if (isTerminalSupportStatus(status)) return false;
  // WAITING / ASSIGNED / ACTIVE / TRANSFERRING / REQUEUED — member may leave messages
  return true;
}

/**
 * WAITING 期間離開頁／上一頁 → ABANDONED，從佇列移除（design §12.1）。
 * 已接通（ACTIVE*）不在此結束 Session；短暫離開走 disconnect／Grace。
 * 伺服器端 CS12：最後一條 IM WS 斷線會自動 mark Grace；此 keepalive 為雙保險。
 */
export async function abandonWaitingSupportSession(session, reason = 'CLIENT_EXPLICIT_CLOSE') {
  if (!session?.sessionId || session.status !== 'WAITING') {
    return null;
  }
  return supportSessionApi.leave(session.sessionId, { reason });
}

/**
 * Unload／unmount 用：keepalive POST，不阻塞導航。
 * React Strict Mode remount 請搭配延遲＋取消（見 SupportChatPage）。
 */
export function abandonWaitingSupportSessionKeepalive(session, reason = 'CLIENT_EXPLICIT_CLOSE') {
  if (typeof window === 'undefined') return false;
  if (!session?.sessionId || session.status !== 'WAITING') return false;
  const token = localStorage.getItem(MEMBER_STORAGE_KEYS.TOKEN);
  if (!token) return false;
  const url = `${IM_API}/customer-service/sessions/${encodeURIComponent(String(session.sessionId))}/leave`;
  try {
    fetch(url, {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reason }),
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

/**
 * ACTIVE* 頁面卸載時標記 Grace（WS 自動掛鉤的雙保險；不等於結束 Session）。
 */
export function markSupportDisconnectKeepalive(session) {
  if (typeof window === 'undefined') return false;
  if (!session?.sessionId) return false;
  const status = session.status;
  if (!status || status === 'WAITING' || status === 'ENDED' || status === 'ABANDONED') {
    return false;
  }
  const token = localStorage.getItem(MEMBER_STORAGE_KEYS.TOKEN);
  if (!token) return false;
  const url = `${IM_API}/customer-service/sessions/${encodeURIComponent(String(session.sessionId))}/disconnect`;
  try {
    fetch(url, {
      method: 'POST',
      headers: {
        Authorization: token,
        'Content-Type': 'application/json',
      },
      body: '{}',
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}
